import { MapPin, BedDouble, Sparkles, UtensilsCrossed, Star, Route, Info } from "lucide-react";
import { BudgetBar } from "@/components/BudgetBar";
import { TransportCompare } from "@/components/TransportCompare";
import { imageFor, eur, isDayTrip } from "@/lib/api";

const Block = ({ icon: Icon, label, title, children, testid }) => (
  <div data-testid={testid} className="rounded-2xl bg-white border border-gray-200 p-6">
    <p className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-amber-600"><Icon className="w-3.5 h-3.5" />{label}</p>
    <h4 className="mt-2 font-serif text-lg font-semibold text-[#0B131E]">{title}</h4>
    <div className="mt-1 text-sm text-gray-600 leading-relaxed">{children}</div>
  </div>
);

export const EscapadeHero = ({ e }) => (
  <div className="relative h-72 md:h-96 rounded-2xl overflow-hidden">
    <img src={imageFor(e)} alt={e.destination} className="w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0B131E] via-[#0B131E]/40 to-transparent" />
    <div className="absolute bottom-0 p-6 md:p-10 text-white max-w-3xl">
      <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold capitalize">{e.angle}</span>
      <h1 data-testid="escapade-title" className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">{e.title}</h1>
      <p className="mt-3 flex items-center gap-1.5 text-sm text-gray-300"><MapPin className="w-4 h-4" />{e.destination}, {e.region} · {e.distance_km} km de {e.departure_city} · {e.duration} jour{e.duration > 1 ? "s" : ""}</p>
    </div>
  </div>
);

export const EscapadeOverview = ({ e, detailedTransport = false }) => (
  <div className="space-y-6">
    <p className="text-base md:text-lg text-gray-700 leading-relaxed">{e.summary}</p>
    <div className="rounded-2xl bg-white border border-gray-200 p-6"><BudgetBar budget={e.budget} userBudget={e.user_budget} testid="detail-budget" /></div>
    <div className="rounded-2xl bg-white border border-gray-200 p-6">
      <p className="font-mono text-[11px] uppercase tracking-widest text-gray-500 mb-3">Train vs voiture · aller-retour groupe</p>
      <TransportCompare transport={e.transport} detailed={detailedTransport} testid="detail-transport" />
    </div>
    <div className="grid sm:grid-cols-3 gap-4">
      {isDayTrip(e) ? (
        <Block icon={BedDouble} label="Hébergement" title="Aller-retour dans la journée" testid="detail-accommodation">
          <p>Pas de nuitée à prévoir : départ le matin, retour le soir.</p>
        </Block>
      ) : (
        <Block icon={BedDouble} label="Hébergement" title={e.accommodation?.name} testid="detail-accommodation">
          <p className="flex items-center gap-1">{e.accommodation?.type} · <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />{e.accommodation?.rating}</p>
          <p>{eur(e.accommodation?.price_per_night)} / nuit · {e.accommodation?.area}</p>
          <p className="mt-1 text-gray-500">{e.accommodation?.highlight}</p>
        </Block>
      )}
      <Block icon={Sparkles} label="Activité principale" title={e.main_activity?.name} testid="detail-activity">
        <p>{e.main_activity?.description}</p><p className="mt-1 font-medium text-gray-800">{eur(e.main_activity?.price)}</p>
      </Block>
      <Block icon={UtensilsCrossed} label="Restauration" title={e.restaurant?.name} testid="detail-restaurant">
        <p>{e.restaurant?.cuisine} · {e.restaurant?.price_range}</p><p className="mt-1 text-gray-500">{e.restaurant?.highlight}</p>
      </Block>
    </div>
    <div data-testid="detail-zone-coherence" className="rounded-2xl bg-emerald-50 border border-emerald-100 p-6 flex gap-3">
      <Route className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
      <div><p className="font-semibold text-emerald-900 text-sm">Cohérence géographique</p><p className="text-sm text-emerald-800 mt-1 leading-relaxed">{e.zone_coherence}</p></div>
    </div>
    {e.zone_note ? (
      <div className="rounded-2xl bg-amber-50 border border-amber-100 p-5 flex gap-3 text-sm text-amber-900"><Info className="w-4 h-4 shrink-0 mt-0.5" />{e.zone_note}</div>
    ) : null}
  </div>
);
